import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ChevronLeft,
  Calculator,
  Target,
  BookOpen,
  Loader2,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  DollarSign,
  Clock,
  Lightbulb,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// AI Opportunity Assessor Component
const AIOpportunityAssessor = () => {
  const [formData, setFormData] = useState({
    process_name: "",
    process_description: "",
    current_challenges: "",
    data_available: "",
    success_metrics: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tools/ai-opportunity-assessment`, formData);
      setResult(response.data.assessment);
    } catch (error) {
      console.error("Assessment error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
          <Target className="w-4 h-4 mr-2" />
          AI-Powered Analysis
        </Badge>
        <h2 className="text-2xl font-bold text-white mb-2">AI Opportunity Assessor</h2>
        <p className="text-[#8a8a8a]">
          Describe a business process and get AI-powered recommendations on automation potential
        </p>
      </div>

      {!result ? (
        <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
          <div>
            <Label className="text-white">Process Name</Label>
            <Input
              value={formData.process_name}
              onChange={(e) => setFormData({ ...formData, process_name: e.target.value })}
              placeholder="e.g., Customer Support Ticket Handling"
              className="bg-[#0a0a0a] border-[#1a1a1a] text-white mt-1"
              required
            />
          </div>

          <div>
            <Label className="text-white">Describe the Process</Label>
            <Textarea
              value={formData.process_description}
              onChange={(e) => setFormData({ ...formData, process_description: e.target.value })}
              placeholder="Walk through what happens step by step. Who does what? How long does it take?"
              className="bg-[#0a0a0a] border-[#1a1a1a] text-white mt-1 min-h-[100px]"
              required
            />
          </div>

          <div>
            <Label className="text-white">Current Challenges</Label>
            <Textarea
              value={formData.current_challenges}
              onChange={(e) => setFormData({ ...formData, current_challenges: e.target.value })}
              placeholder="What's frustrating about this process? What goes wrong? Where are the bottlenecks?"
              className="bg-[#0a0a0a] border-[#1a1a1a] text-white mt-1"
              required
            />
          </div>

          <div>
            <Label className="text-white">Data Available (Optional)</Label>
            <Textarea
              value={formData.data_available}
              onChange={(e) => setFormData({ ...formData, data_available: e.target.value })}
              placeholder="What data do you have? CRM records, historical logs, spreadsheets, etc."
              className="bg-[#0a0a0a] border-[#1a1a1a] text-white mt-1"
            />
          </div>

          <div>
            <Label className="text-white">Success Metrics (Optional)</Label>
            <Input
              value={formData.success_metrics}
              onChange={(e) => setFormData({ ...formData, success_metrics: e.target.value })}
              placeholder="e.g., Response time, error rate, cost per transaction"
              className="bg-[#0a0a0a] border-[#1a1a1a] text-white mt-1"
            />
          </div>

          <Button type="submit" className="w-full btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Get AI Assessment
              </>
            )}
          </Button>
        </form>
      ) : (
        <div className="max-w-3xl mx-auto">
          <Card className="bg-[#0a0a0a] border-[#1a1a1a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Assessment Complete: {formData.process_name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="prose prose-invert max-w-none text-[#c0c0c0]"
                dangerouslySetInnerHTML={{ 
                  __html: result
                    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                    .replace(/\n/g, '<br/>')
                }}
              />
            </CardContent>
          </Card>
          <Button 
            onClick={() => setResult(null)} 
            variant="outline" 
            className="mt-4 border-[#1a1a1a]"
          >
            Assess Another Process
          </Button>
        </div>
      )}
    </div>
  );
};

// ROI Calculator Component
const ROICalculator = () => {
  const [formData, setFormData] = useState({
    process_name: "",
    hours_per_week: 20,
    hourly_cost: 50,
    volume_per_month: 1000,
    error_rate_percent: 5,
    ai_accuracy_target: 90,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tools/roi-calculator`, formData);
      setResult(response.data);
    } catch (error) {
      console.error("ROI calculation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30">
          <Calculator className="w-4 h-4 mr-2" />
          Financial Analysis
        </Badge>
        <h2 className="text-2xl font-bold text-white mb-2">AI ROI Calculator</h2>
        <p className="text-[#8a8a8a]">
          Estimate the potential return on investment for automating a process
        </p>
      </div>

      {!result ? (
        <form onSubmit={handleSubmit} className="space-y-6 max-w-xl mx-auto">
          <div>
            <Label className="text-white">Process Name</Label>
            <Input
              value={formData.process_name}
              onChange={(e) => setFormData({ ...formData, process_name: e.target.value })}
              placeholder="e.g., Invoice Processing"
              className="bg-[#0a0a0a] border-[#1a1a1a] text-white mt-1"
              required
            />
          </div>

          <div>
            <Label className="text-white">Hours Spent Per Week: {formData.hours_per_week}</Label>
            <Slider
              value={[formData.hours_per_week]}
              onValueChange={(v) => setFormData({ ...formData, hours_per_week: v[0] })}
              min={1}
              max={100}
              step={1}
              className="mt-2"
            />
          </div>

          <div>
            <Label className="text-white">Average Hourly Cost ($): {formData.hourly_cost}</Label>
            <Slider
              value={[formData.hourly_cost]}
              onValueChange={(v) => setFormData({ ...formData, hourly_cost: v[0] })}
              min={15}
              max={200}
              step={5}
              className="mt-2"
            />
          </div>

          <div>
            <Label className="text-white">Monthly Volume (transactions): {formData.volume_per_month}</Label>
            <Slider
              value={[formData.volume_per_month]}
              onValueChange={(v) => setFormData({ ...formData, volume_per_month: v[0] })}
              min={100}
              max={50000}
              step={100}
              className="mt-2"
            />
          </div>

          <div>
            <Label className="text-white">Current Error Rate: {formData.error_rate_percent}%</Label>
            <Slider
              value={[formData.error_rate_percent]}
              onValueChange={(v) => setFormData({ ...formData, error_rate_percent: v[0] })}
              min={0}
              max={20}
              step={0.5}
              className="mt-2"
            />
          </div>

          <Button type="submit" className="w-full btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <TrendingUp className="w-4 h-4 mr-2" />
                Calculate ROI
              </>
            )}
          </Button>
        </form>
      ) : (
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Summary Card */}
          <Card className={`border-2 ${result.roi_analysis.year1_roi_percent > 50 ? 'border-green-500/50 bg-green-500/5' : result.roi_analysis.year1_roi_percent > 0 ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-red-500/50 bg-red-500/5'}`}>
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-bold text-white mb-2">{result.recommendation}</h3>
              <div className="text-4xl font-bold text-green-400 mb-1">
                {result.roi_analysis.year1_roi_percent}%
              </div>
              <p className="text-[#8a8a8a]">First Year ROI</p>
            </CardContent>
          </Card>

          {/* Details Grid */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="bg-[#0a0a0a] border-[#1a1a1a]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#8a8a8a]">Current State</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-[#8a8a8a]">Annual Hours</span>
                    <span className="text-white">{result.current_state.annual_hours}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8a8a8a]">Labor Cost</span>
                    <span className="text-white">{formatCurrency(result.current_state.annual_labor_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8a8a8a]">Error Cost</span>
                    <span className="text-white">{formatCurrency(result.current_state.estimated_error_cost)}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-[#1a1a1a]">
                    <span className="text-white">Total</span>
                    <span className="text-red-400">{formatCurrency(result.current_state.total_annual_cost)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a0a0a] border-[#1a1a1a]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#8a8a8a]">With AI</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-[#8a8a8a]">Hours Saved</span>
                    <span className="text-green-400">{result.projected_with_ai.hours_saved_annually}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8a8a8a]">Labor Savings</span>
                    <span className="text-green-400">{formatCurrency(result.projected_with_ai.labor_savings)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8a8a8a]">Error Savings</span>
                    <span className="text-green-400">{formatCurrency(result.projected_with_ai.error_reduction_savings)}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-[#1a1a1a]">
                    <span className="text-white">Annual Savings</span>
                    <span className="text-green-400">{formatCurrency(result.projected_with_ai.total_annual_savings)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a0a0a] border-[#1a1a1a]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#8a8a8a]">Investment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-[#8a8a8a]">Implementation</span>
                    <span className="text-white">{formatCurrency(result.investment.estimated_implementation)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8a8a8a]">Annual Platform</span>
                    <span className="text-white">{formatCurrency(result.investment.annual_platform_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8a8a8a]">Payback</span>
                    <span className="text-blue-400">{result.roi_analysis.payback_months} months</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-[#1a1a1a]">
                    <span className="text-white">5-Year Net</span>
                    <span className="text-green-400">{formatCurrency(result.roi_analysis["5_year_net_benefit"])}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Button 
            onClick={() => setResult(null)} 
            variant="outline" 
            className="border-[#1a1a1a]"
          >
            Calculate Another
          </Button>
        </div>
      )}
    </div>
  );
};

// Case Study Simulator Component
const CaseStudySimulator = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCase, setActiveCase] = useState(null);
  const [currentDecision, setCurrentDecision] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [completed, setCompleted] = useState([]);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/tools/case-studies`);
      setCaseStudies(response.data.case_studies);
      setCompleted(response.data.completed || []);
    } catch (error) {
      console.error("Error fetching case studies:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    const decision = activeCase.decisions[currentDecision];
    const selected = decision.options.find(o => o.id === option);
    setFeedback({
      text: selected.feedback,
      isCorrect: option === decision.best_answer
    });
  };

  const handleNextDecision = async () => {
    if (currentDecision < activeCase.decisions.length - 1) {
      setCurrentDecision(currentDecision + 1);
      setSelectedOption(null);
      setFeedback(null);
    } else {
      // Complete case study
      try {
        await axios.post(`${API}/tools/case-study/submit`, {
          scenario_id: activeCase.id,
          decision: "completed"
        });
        setCompleted([...completed, activeCase.id]);
      } catch (error) {
        console.error("Error saving progress:", error);
      }
      setActiveCase(null);
      setCurrentDecision(0);
      setSelectedOption(null);
      setFeedback(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (activeCase) {
    const decision = activeCase.decisions[currentDecision];
    const isLastDecision = currentDecision === activeCase.decisions.length - 1;

    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <Button 
          variant="ghost" 
          onClick={() => {
            setActiveCase(null);
            setCurrentDecision(0);
            setSelectedOption(null);
            setFeedback(null);
          }}
          className="text-[#8a8a8a]"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Cases
        </Button>

        <Card className="bg-[#0a0a0a] border-[#1a1a1a]">
          <CardHeader>
            <Badge className="w-fit mb-2 bg-purple-500/20 text-purple-400">
              {activeCase.industry}
            </Badge>
            <CardTitle className="text-xl text-white">{activeCase.title}</CardTitle>
            <CardDescription className="text-[#8a8a8a]">
              {activeCase.scenario}
            </CardDescription>
          </CardHeader>
        </Card>

        <Card className="bg-[#0a0a0a] border-[#1a1a1a]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Decision {currentDecision + 1} of {activeCase.decisions.length}</CardTitle>
              <Progress value={((currentDecision + 1) / activeCase.decisions.length) * 100} className="w-24 h-2" />
            </div>
            <p className="text-lg text-white mt-4">{decision.question}</p>
          </CardHeader>
          <CardContent>
            <RadioGroup value={selectedOption} onValueChange={handleOptionSelect} disabled={!!feedback}>
              {decision.options.map((option) => (
                <div
                  key={option.id}
                  className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors mb-2 ${
                    feedback
                      ? option.id === decision.best_answer
                        ? "border-green-500 bg-green-500/10"
                        : selectedOption === option.id
                        ? "border-red-500 bg-red-500/10"
                        : "border-[#1a1a1a]"
                      : selectedOption === option.id
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-[#1a1a1a] hover:border-[#2a2a2a]"
                  }`}
                >
                  <RadioGroupItem value={option.id} id={option.id} />
                  <Label htmlFor={option.id} className="flex-1 cursor-pointer text-[#c0c0c0]">
                    {option.text}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {feedback && (
              <div className={`mt-4 p-4 rounded-lg ${feedback.isCorrect ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                <p className={`font-semibold ${feedback.isCorrect ? 'text-green-400' : 'text-yellow-400'}`}>
                  {feedback.isCorrect ? '✓ Great choice!' : '⚠️ Consider this:'}
                </p>
                <p className="text-[#c0c0c0] mt-1">{feedback.text}</p>
              </div>
            )}

            {feedback && (
              <Button onClick={handleNextDecision} className="mt-4 btn-primary">
                {isLastDecision ? 'Complete Case Study' : 'Next Decision'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>

        {isLastDecision && feedback && (
          <Card className="bg-blue-500/5 border-blue-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-blue-400" />
                Key Takeaways
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {activeCase.learning_points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#c0c0c0]">
                    <CheckCircle className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <Badge className="mb-4 bg-purple-500/20 text-purple-400 border-purple-500/30">
          <BookOpen className="w-4 h-4 mr-2" />
          Interactive Learning
        </Badge>
        <h2 className="text-2xl font-bold text-white mb-2">Case Study Simulator</h2>
        <p className="text-[#8a8a8a]">
          Practice real-world AI decision making with interactive scenarios
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {caseStudies.map((cs) => (
          <Card 
            key={cs.id} 
            className={`bg-[#0a0a0a] border-[#1a1a1a] cursor-pointer transition-all hover:border-purple-500/50 ${completed.includes(cs.id) ? 'opacity-70' : ''}`}
            onClick={() => setActiveCase(cs)}
          >
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <Badge variant="outline" className="border-[#2a2a2a]">{cs.industry}</Badge>
                {completed.includes(cs.id) && (
                  <Badge className="bg-green-500/20 text-green-400">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Completed
                  </Badge>
                )}
              </div>
              <CardTitle className="text-white">{cs.title}</CardTitle>
              <CardDescription className="text-[#8a8a8a] line-clamp-3">
                {cs.scenario}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm text-[#8a8a8a]">
                <span>{cs.decisions.length} decisions</span>
                <span>{cs.learning_points.length} takeaways</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Main Tools Page Component
const ToolsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-6">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold gradient-text mb-2">Practical AI Tools</h1>
          <p className="text-[#8a8a8a]">Apply what you've learned to your business</p>
        </div>

        <Tabs defaultValue="assessor" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8 bg-[#0a0a0a] border border-[#1a1a1a]">
            <TabsTrigger value="assessor" className="data-[state=active]:bg-blue-500">
              <Target className="w-4 h-4 mr-2" />
              AI Assessor
            </TabsTrigger>
            <TabsTrigger value="calculator" className="data-[state=active]:bg-green-500">
              <Calculator className="w-4 h-4 mr-2" />
              ROI Calculator
            </TabsTrigger>
            <TabsTrigger value="cases" className="data-[state=active]:bg-purple-500">
              <BookOpen className="w-4 h-4 mr-2" />
              Case Studies
            </TabsTrigger>
          </TabsList>

          <TabsContent value="assessor">
            <AIOpportunityAssessor />
          </TabsContent>

          <TabsContent value="calculator">
            <ROICalculator />
          </TabsContent>

          <TabsContent value="cases">
            <CaseStudySimulator />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ToolsPage;
